/**
 * src/main.tsx — SPA entry point (replaces src/start.ts / SSR entry)
 */
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { getRouter } from "./router";
import { scrubBreadcrumb, scrubEvent, sentryDataCollection } from "./lib/sentry";

import "./styles.css";

// Error monitoring: enabled only when VITE_SENTRY_DSN is set (set it in .env.production)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    dataCollection: sentryDataCollection,   // v11 defaults collect user info, cookies, headers, bodies and query strings
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
}

// One shared QueryClient for the entire application.
// Passed into both QueryClientProvider and the TanStack Router context
// so route guards (beforeLoad) and component hooks (useQuery) share
// the same cache — preventing duplicate API calls.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const router = getRouter(queryClient);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
